import i18next from "utilities/i18n";

const FALLBACK_B = "B";
const FALLBACK_M = "M";
const FALLBACK_K = "k";

export const formatNumber = (input: any) => {
  if (typeof input !== "number") {
    return input;
  }

  return Number(input).toLocaleString();
};

export interface HumanReadableOutput {
  hrOutput: string | undefined;
  fmtOutput: string | undefined;
  isAbbreviated: boolean;
}

/**
 * Provides "human readable" versions of large numbers.
 * Returns an object with three properties:
 * {
 *   hrOutput: A string that may be brief (eg, "12M"), formatted to be brief / human readable
 *   fmtOutput: A string that is formatted with commas, etc.
 *   isAbbreviated: Flag that indicates whether the hrOutput is "abbreviated" (eg, "12M" for 12 million)
 * }
 *
 * @param input - the number to be formatted.  (Passing a string returns the string unchanged)
 * @param roundNumbersUnder10K - whether to round numbers under 9,999.5 to the nearest whole number.
 *                               ONLY relevant if transformNumbersUnder10K is false
 * @param transformNumbersUnder10K - parse numbers under 10,000 into the "10k" format
 */
export const humanReadable = (
  input: string | number | undefined,
  roundNumbersUnder10K: boolean = true,
  transformNumbersUnder10K: boolean = false
): HumanReadableOutput => {
  if (!roundNumbersUnder10K && transformNumbersUnder10K) {
    console.warn(
      "WARNING: It is recommended to set roundNumbersUnder10K to true when transformNumbersUnder10K is true"
    );
  }

  if (typeof input !== "number") {
    return {
      hrOutput: input ? `${input}` : "",
      fmtOutput: input ? `${input}` : "",
      isAbbreviated: false
    };
  }

  const smallThreshold = transformNumbersUnder10K ? 999 : 9999.5;

  if (input < smallThreshold) {
    const output = roundNumbersUnder10K ? truncDecimals(input) : input;
    return {
      hrOutput: Number(output).toLocaleString(),
      fmtOutput: formatNumber(input),
      isAbbreviated: false
    };
  }

  if (input < 999950) {
    const coeff = parseFloat((input / 1000.0).toFixed(1));
    const suffix = i18next.t("HR_SUFFIX_K") ?? FALLBACK_K;
    return {
      hrOutput: Number(coeff).toLocaleString() + suffix,
      fmtOutput: formatNumber(input),
      isAbbreviated: true
    };
  }

  if (input < 999999950) {
    const coeff = parseFloat((input / 1000000.0).toFixed(1));
    const suffix = i18next.t("HR_SUFFIX_M") ?? FALLBACK_M;
    return {
      hrOutput: Number(coeff).toLocaleString() + suffix,
      fmtOutput: formatNumber(input),
      isAbbreviated: true
    };
  }

  const coeff = parseFloat((input / 1000000000.0).toFixed(1));
  const suffix = i18next.t("HR_SUFFIX_B") ?? FALLBACK_B;
  return {
    hrOutput: Number(coeff).toLocaleString() + suffix,
    fmtOutput: formatNumber(input),
    isAbbreviated: true
  };
};

/**
 * Truncates a float to the given number of decimals.
 */
export const truncDecimals = (value: number, precision?: number) => {
  if (precision && value < 1) {
    return parseFloat(value.toFixed(precision));
  }

  return Math.round(value);
};

export const roundAny = (value: number | any, forceToNumber = true) => {
  let val = +value;
  if (isNaN(val)) {
    if (!forceToNumber) {
      return value;
    }

    val = 0;
  }

  return Math.round(val);
};

const byteUnits = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

export const humanReadableBytes = (num: number) => {
  let neg = num < 0;
  num = Math.abs(num);
  const symbol = neg ? "-" : "";

  if (num < 1) {
    return `${symbol}${num.toLocaleString()} B`;
  }

  let exponent = Math.min(
    Math.floor(Math.log(num) / Math.log(1000)),
    byteUnits.length - 1
  );
  num = Number(num / Math.pow(1000, exponent));
  if (round(num, 0) > 999 && exponent < byteUnits.length - 1) {
    num = num / 1000;
    exponent++;
  }

  const unit = byteUnits[exponent];

  if (num >= 10 || num % 1 === 0) {
    // Do not show decimals when the number is two-digit, or if the number has no
    // decimal component.
    return `${symbol}${Number(round(num, 0)).toLocaleString()} ${unit}`;
  }

  return `${symbol}${Number(round(num, 1)).toLocaleString()} ${unit}`;
};

function round(value: number, precision: number) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

export const toNumber = (s: string) => {
  const n = parseInt(s);
  return isNaN(n) ? 0 : n; //Display undefined initial values as zero for number inputs
};
